import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { ListResponse } from "@models/list-response";
import { Album, AlbumType } from "@models/album";
import { PageResponse } from "@models/page-response";
import { Photo } from "@models/photo";
import { AlbumFilters } from "@models/album-filters";
import { accountSetupApi } from "@store/accountSetupApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

interface CreateAlbumDto {
  patientId: string;
  name: string;
  photoIds: string[];
  albumType: AlbumType;
  coverPhotoId?: string;
}

interface UpdateAlbumDto {
  id: string;
  name?: string;
  coverPhotoId?: string;
}

export const albumApi = createApi({
  reducerPath: "albumApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/albums`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Album", "Photo", "Filters"],
  endpoints: (builder) => ({
    getAlbums: builder.query<
      ListResponse<Album>,
      { patientId: string; search?: string }
    >({
      providesTags: ["Album"],
      query: (params) => {
        return {
          url: "",
          method: "GET",
          params,
        };
      },
    }),
    getAlbum: builder.query<Album, { id: string }>({
      providesTags: ["Album"],
      query: ({ id }) => {
        return {
          url: id,
          method: "GET",
        };
      },
    }),
    deleteAlbum: builder.mutation<void, string>({
      invalidatesTags: ["Album"],
      query: (id: string) => {
        return {
          url: id,
          method: "DELETE",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
      },
    }),
    createAlbum: builder.mutation<void, CreateAlbumDto>({
      invalidatesTags: ["Album"],
      query: ({ patientId, ...album }) => {
        return {
          url: "",
          method: "POST",
          body: album,
          params: { patientId },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
      },
    }),
    updateAlbum: builder.mutation<void, UpdateAlbumDto>({
      invalidatesTags: ["Album"],
      query: ({ id, ...album }) => {
        return {
          url: id,
          method: "PATCH",
          body: album,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
      },
    }),
    deleteAlbumPhotos: builder.mutation<
      void,
      { photoIds: string[]; albumId: string }
    >({
      invalidatesTags: ["Photo", "Album"],
      query: ({ albumId, photoIds }) => {
        return {
          url: `${albumId}/photos`,
          method: "DELETE",
          body: { photoIds },
        };
      },
    }),
    addAlbumPhotos: builder.mutation<
      void,
      { photoIds: string[]; albumId: string }
    >({
      invalidatesTags: ["Photo", "Album"],
      query: ({ albumId, photoIds }) => {
        return {
          url: `${albumId}/photos`,
          method: "POST",
          body: { photoIds },
        };
      },
    }),
    getAlbumPhotos: builder.query<
      PageResponse<Photo>,
      {
        albumId: string;
      }
    >({
      providesTags: ["Photo"],
      query: ({ albumId }) => {
        return {
          url: `${albumId}/photos`,
          method: "GET",
          params: {
            page: 1,
            pageSize: 150,
          },
        };
      },
    }),
    getAlbumFilters: builder.query<
      AlbumFilters,
      {
        albumId: string;
      }
    >({
      providesTags: ["Filters"],
      query: ({ albumId }) => {
        return {
          url: `${albumId}/filters`,
          method: "GET",
        };
      },
    }),
    updateAlbumFilters: builder.mutation<
      AlbumFilters,
      AlbumFilters & { albumId: string }
    >({
      invalidatesTags: ["Filters"],
      query: ({ albumId, ...filters }) => {
        return {
          url: `${albumId}/filters`,
          method: "PATCH",
          body: filters,
        };
      },
    }),
  }),
});

export const {
  useGetAlbumsQuery,
  useGetAlbumQuery,
  useDeleteAlbumMutation,
  useCreateAlbumMutation,
  useUpdateAlbumMutation,
  useDeleteAlbumPhotosMutation,
  useAddAlbumPhotosMutation,
  useGetAlbumPhotosQuery,
  useGetAlbumFiltersQuery,
  useUpdateAlbumFiltersMutation,
} = albumApi;
